import { notificationsOptions } from '@/shared/config/notifications';

const state = () => ({
  notifications: [],
});

const getters = {
  allNotifications: (state) => state.notifications,
};

let notificationIdCounter = 0;

const mutations = {
  ADD_NOTIFICATION(state, notification) {
    state.notifications.push(notification);
  },
  REMOVE_NOTIFICATION(state, id) {
    state.notifications = state.notifications.filter((n) => n.id !== id);
  },
};

const actions = {
  generateUniqueId() {
    notificationIdCounter += 1;
    return `notification-${notificationIdCounter}`;
  },

  /**
   * Shows a new notification.
   * @param {object} payload - The notification payload.
   * @param {string} payload.type - Type of notification (e.g., 'success', 'error').
   * @param {string} payload.title - The notification title.
   * @param {string} payload.message - The notification message.
   * @param {number} [payload.duration] - Optional duration in milliseconds for auto-close.
   * @param payload.commit
   * @param payload.dispatch
   * @param root1
   * @param root1.type
   * @param root1.title
   * @param root1.message
   * @param root1.duration
   * @returns {string} The ID of the added notification.
   */
  async showNotification({ commit, dispatch }, { type, title, message, duration = notificationsOptions.duration }) {
    const id = await dispatch('generateUniqueId');
    const notification = { id, type, title, message, duration };
    commit('ADD_NOTIFICATION', notification);
    return id;
  },

  /**
   * Shows an error notification from an error object.
   * @param {object} payload - The error payload.
   * @param {Error} payload.error - The error object.
   * @param {string} [payload.defaultTitle] - Default title if not found in error.
   * @param {string} [payload.defaultMessage] - Default message if not found in error.
   * @param {number} [payload.duration] - Optional duration in milliseconds for auto-close.
   * @param payload.dispatch
   * @param root1
   * @param root1.error
   * @param root1.defaultTitle
   * @param root1.defaultMessage
   * @param root1.duration
   * @param root1.title
   * @param root1.message
   * @param payload.commit
   * @returns {string} The ID of the added notification.
   */
  async showNotificationErrorMessage(
    { commit, dispatch },
    {
      error,
      title = 'Error',
      message = 'Oops! Something went wrong',
      duration = notificationsOptions.duration
    }
  ) {
    let notificationTitle = title;
    let notificationMessage = message;

    if (error.response) {
      const responseData = error.response.data;

      if (responseData.title) notificationTitle = responseData.title;

      if (responseData.status === 'validation' && responseData.data) {
        const validationErrors = responseData.data;
        notificationMessage = Object.values(validationErrors)
          .flat()
          .join('\n');
      } else if (responseData.message) {
        notificationMessage = responseData.message;
      } else if (responseData.error) {
        notificationMessage = responseData.error;
      }
    } else if (error.message) {
      notificationMessage = error.message;
    }

    const id = await dispatch('generateUniqueId');
    const notification = { id, type: 'error', title: notificationTitle, message: notificationMessage, duration };
    commit('ADD_NOTIFICATION', notification);

    return id;
  },

  /**
   * Removes a notification by its ID.
   * @param {string} id - The ID of the notification to remove.
   */
  removeNotification({ commit }, id) {
    commit('REMOVE_NOTIFICATION', id);
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
